import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import spIcon from "../../assets/brand/swlogo.png";
import spWordmark from "../../assets/brand/swletters.png";
import api from "../../api/api";
import socket from "../../socket";
import { toast } from "react-toastify";
import { useAuth } from "../../context/useAuth";

export const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!email || !password || !isChecked) {
    toast.warn("Please fill in all fields and accept the terms");
    return;
  }

  try {
    setSubmitting(true);

    // Call backend login
    const data = await api.auth.login(email, password);

    // Check vendor role
    if (data?.user?.role !== "vendor") {
      toast.warn("You are not a vendor");
      return;
    }

    // ⭐ USE CONTEXT LOGIN (this stores token + decodes JWT)
    const mergedUser = login(data.token, data.user);

    if (!mergedUser) {
      toast.error("Failed to authenticate user.");
      return;
    }

    // Remember-me
    if (remember) localStorage.setItem("sp_remember", "1");
    else localStorage.removeItem("sp_remember");

    // Register socket
    if (mergedUser?.userId || data.user?._id) {
      socket.emit("registerUser", { userId: mergedUser.userId || data.user._id });
    }

    toast.success("Login successful!");
    navigate("/dashboard");

  } catch (err) {
    console.warn("Login failed:", err);
    toast.warn(err?.data?.message || "Login failed. Please check your credentials.");
  } finally {
    setSubmitting(false);
  }
};

  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
     
      <div className="w-[412px]">

        {/* Logo */}
        <div className="w-full flex justify-center">
          <div className="inline-flex items-center gap-[0px] leading-none">
            <img
              src={spIcon}
              alt="Sweet Plate logo"
              className="shrink-0 select-none object-contain w-[100px] h-[86px] -mt-[2px]"
            />
            <img
              src={spWordmark}
              alt="Sweet Plate"
              className="shrink-0 select-none object-contain h-[50px] w-auto -ml-[4px]"
            />
          </div>
        </div>

        {/* Heading + subheading */}
        <h1 className="mt-4 text-center [font-family:'Nunito',Helvetica] font-bold text-[#5d4037] text-2xl leading-9">
          Welcome Back
        </h1>
        <p className="mt-2 w-[358px] mx-auto text-center [font-family:'Nunito',Helvetica] font-medium text-[#5d4037] text-base leading-6">
          Ready to serve again? Log in to update your
          <br /> menu and connect with customers.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-8 [font-family:'Nunito',Helvetica] text-[#5d4037]">

          <div className="w-[378px] mx-auto">
            <label className="block text-base font-semibold mb-3">Email address</label>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-[60px] bg-[#fff8e1] rounded-[18px] border border-[#5d4037] px-[17px] py-[18px] text-[#7a6f5c] text-base leading-[22px] outline-none"
            />
          </div>

          {/* Password */}
          <div className="w-[378px] mx-auto mt-6">
            <label className="block text-base font-semibold mb-3">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-[60px] bg-[#fff8e1] rounded-[18px] border border-[#5d4037] pl-[17px] pr-12 py-[18px] text-[#7a6f5c] text-base leading-[22px] outline-none"
              />
      
              <button
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-4 top-1/2 -translate-y-1/2"
              >
             
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
                    <path d="M3 3l18 18" />
                    <path d="M9.9 4.2A11 11 0 0 1 21 12s-4 7-11 7a11 11 0 0 1-6.1-2.1" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Remember / Forgot row */}
          <div className="w-[378px] mx-auto mt-4 flex items-center justify-between">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-5 h-5 rounded border border-[#5d4037] accent-[#5d4037]"
              />
              <span className="text-base">Remember me</span>
            </label>
            <Link to="/forgot-password" className="text-base underline text-[#663114] hover:opacity-80">
              Forgot Password?
            </Link>
          </div>

          {/* Terms */}
          <div className="w-[378px] mx-auto mt-4 flex gap-2">
            <input
              id="login-terms"
              type="checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded border border-[#5d4037] accent-[#5d4037]"
            />
            <label htmlFor="login-terms" className="text-sm text-[#62552c]">
              I certify that I am 18 years of age or older, and I agree to the{" "}
              <span className="text-[#663114] underline">User Agreement</span>
              {" "}and{" "}
              <span className="text-[#663114] underline">Privacy Policy</span>.
            </label>
          </div>

          {/* Button */}
          <div className="w-[378px] mx-auto">
            <button
              type="submit"
              disabled={submitting} 
              className={`mt-6 w-full h-[60px] rounded-[50px] font-bold text-[#5d4037] text-base leading-[22px] transition-opacity 
              ${submitting ? "bg-[#d9c48c] cursor-not-allowed" : "bg-[#f7c945] hover:opacity-90"}`}
            >
              {submitting ? "Signing in..." : "Login"}
            </button>
          </div>
 
          <p className="w-full text-center mt-7 text-base">
            <span className="font-medium text-[#a99451]">Haven't registered yet?</span>
            <span className="font-medium text-[#1f233673]"> </span>
            <Link to="/signup" className="font-semibold text-[#663114] underline hover:opacity-80">
              Register
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};
